import { useClickOutside } from '@creator/ui/hooks/useClickOutside';
import { cn } from '@creator/ui/utils/ui';
// OffCanvasMenuContent.tsx
import type React from 'react';
import { useContext, useRef } from 'react';
import { OffCanvasMenuContext } from './OffCanvasMenu';

export interface OffCanvasMenuContentProps {
    children: React.ReactNode;
    className?: string;
}

/**
 * The off‑canvas menu panel that slides into view.
 * It also renders a backdrop overlay that closes the menu when clicked.
 */
export const OffCanvasMenuContent: React.FC<OffCanvasMenuContentProps> = props => {
    const { children, className } = props;
    const context = useContext(OffCanvasMenuContext);

    if (!context) throw new Error('OffCanvasMenuContent must be used within an OffCanvasMenu component.');

    const { open, setOpen, align } = context;
    const panelRef = useRef<HTMLDivElement>(null);

    // Close the menu if a click is detected outside the panel.
    useClickOutside(panelRef, () => {
        setOpen(false);
    });

    // Determine the transform class for sliding the panel in/out.
    let transformClass = '';
    if (align === 'left') transformClass = open ? 'translate-x-0' : '-translate-x-full';
    else transformClass = open ? 'translate-x-0' : 'translate-x-full';

    // Determine the side class based on alignment.
    const sideClass = align === 'left' ? 'left-0' : 'right-0';

    return (
        <>
            {open && (
                // Backdrop overlay
                <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}>
                    <div className="absolute inset-0 bg-black opacity-50" />
                </div>
            )}
            <div
                ref={panelRef}
                className={cn(
                    'transition fixed overflow-auto top-0 bottom-0 w-3/4 lg:w-80 bg-gray-50 dark:bg-gray-950 shadow-xl z-50 transform ',
                    sideClass,
                    transformClass,
                    className
                )}
            >
                {children}
            </div>
        </>
    );
};
